const COLOR_LIGHT = [165, 176, 37];
const COLOR_DARK = [54, 86, 43];
const PROFILES = {
    hero: {
        threadPairs: 18,
        maxRenderPixels: 1800000,
        minRenderScale: .5,
        mobileSegments: 110,
        desktopSegments: 190,
        lowPowerSegments: 150,
        frameInterval: 0,
        lowPowerFrameInterval: 1000 / 30,
        speed: .00032,
    },
    lineup: {
        threadPairs: 18,
        maxRenderPixels: 1800000,
        minRenderScale: .5,
        mobileSegments: 110,
        desktopSegments: 190,
        lowPowerSegments: 150,
        frameInterval: 1000 / 30,
        lowPowerFrameInterval: 1000 / 20,
        speed: .00032,
    },
    ambient: {
        threadPairs: 12,
        maxRenderPixels: 900000,
        minRenderScale: .45,
        mobileSegments: 80,
        desktopSegments: 112,
        lowPowerSegments: 92,
        frameInterval: 1000 / 24,
        lowPowerFrameInterval: 1000 / 20,
        speed: .00036,
    },
};

export default class FestivalHeroAnimation {
    constructor($el) {
        this.$el = $el;
        this.$visibilityTarget = $el.parentElement || $el;
        this.ctx = $el.getContext('2d');
        this.profile = this.getProfile();
        this.threads = [];
        this.isVisible = false;
        this.isPageVisible = !document.hidden;
        this.isReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.frame = null;
        this.resizeFrame = null;
        this.lastFrameTime = 0;
        this.frameInterval = this.profile.frameInterval;
        this.queueResize = this.queueResize.bind(this);
        this.animate = this.animate.bind(this);
        this.checkVisibility = this.checkVisibility.bind(this);
        this.checkPageVisibility = this.checkPageVisibility.bind(this);

        if (!this.ctx) {
            return;
        }

        this.createThreads();
        this.resize();

        this.observe();

        if (this.isReducedMotion) {
            this.draw(0);
        }
    }

    getProfile() {
        if (
            this.$el.classList.contains('speaker-line-up-block__animation')
            || this.$el.classList.contains('program-timetable__animation')
            || this.$el.classList.contains('news-list-block__animation')
            || this.$el.classList.contains('news-detail-block__animation')
            || this.$el.classList.contains('speaker-detail-block__animation')
        ) {
            return PROFILES.lineup;
        }

        if (this.$el.classList.contains('festival-hero-block__animation')) {
            return PROFILES.hero;
        }

        return PROFILES.ambient;
    }

    createThreads() {
        for (let i = 0; i < this.profile.threadPairs; i++) {
            this.threads.push({
                color: COLOR_LIGHT,
                phase: (i / this.profile.threadPairs) * 2.5,
                timeOffset: 0,
            });

            this.threads.push({
                color: COLOR_DARK,
                phase: (i / this.profile.threadPairs) * 2.5,
                timeOffset: Math.PI * .8,
            });
        }
    }

    observe() {
        window.addEventListener('resize', this.queueResize);
        document.addEventListener('visibilitychange', this.checkPageVisibility);

        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(this.queueResize);
            this.resizeObserver.observe(this.$el);
        }

        if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(this.checkVisibility, {
                rootMargin: '120px 0px',
            });
            this.observer.observe(this.$visibilityTarget);
            return;
        }

        this.isVisible = true;
        this.updatePlayback();
    }

    checkVisibility(entries) {
        entries.forEach(entry => {
            this.isVisible = entry.isIntersecting;
        });

        this.updatePlayback();
    }

    checkPageVisibility() {
        this.isPageVisible = !document.hidden;
        this.updatePlayback();
    }

    updatePlayback() {
        if (this.isReducedMotion) {
            return;
        }

        if (this.isVisible && this.isPageVisible) {
            this.start();
        } else {
            this.stop();
        }
    }

    start() {
        if (this.frame) {
            return;
        }

        this.lastFrameTime = 0;
        this.frame = window.requestAnimationFrame(this.animate);
    }

    stop() {
        if (!this.frame) {
            return;
        }

        window.cancelAnimationFrame(this.frame);
        this.frame = null;
    }

    queueResize() {
        if (this.resizeFrame) {
            return;
        }

        this.resizeFrame = window.requestAnimationFrame(() => {
            this.resizeFrame = null;
            this.resize();
        });
    }

    resize() {
        const rect = this.$el.getBoundingClientRect();
        const width = Math.max(1, Math.round(rect.width));
        const height = Math.max(1, Math.round(rect.height));
        const renderScale = this.getRenderScale(width, height);
        const canvasWidth = Math.round(width * renderScale);
        const canvasHeight = Math.round(height * renderScale);

        if (
            this.width === width
            && this.height === height
            && this.$el.width === canvasWidth
            && this.$el.height === canvasHeight
        ) {
            return;
        }

        this.width = width;
        this.height = height;
        this.renderScale = renderScale;
        this.frameInterval = this.getFrameInterval();
        this.segments = this.getSegmentCount();
        this.$el.width = canvasWidth;
        this.$el.height = canvasHeight;
        this.ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
        this.draw(this.isReducedMotion ? 0 : performance.now() * this.profile.speed);
    }

    getRenderScale(width, height) {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const preferredScale = Math.max(dpr, this.profile.preferredRenderScale || 0);
        const maxScale = Math.sqrt(this.profile.maxRenderPixels / (width * height));

        return Math.max(this.profile.minRenderScale, Math.min(preferredScale, maxScale));
    }

    getFrameInterval() {
        if (this.renderScale < 1 || this.isLowPowerDevice()) {
            return this.profile.lowPowerFrameInterval;
        }

        return this.profile.frameInterval;
    }

    getSegmentCount() {
        if (this.width < 768) {
            return this.profile.mobileSegments;
        }

        return this.renderScale < 1 || this.isLowPowerDevice()
            ? this.profile.lowPowerSegments
            : this.profile.desktopSegments;
    }

    isLowPowerDevice() {
        const cores = navigator.hardwareConcurrency || 0;
        const memory = navigator.deviceMemory || 0;

        return (cores > 0 && cores <= 4) || (memory > 0 && memory <= 4);
    }

    animate(now) {
        if (!this.isVisible || !this.isPageVisible) {
            this.frame = null;
            return;
        }

        if (!this.lastFrameTime || now - this.lastFrameTime >= this.frameInterval) {
            this.draw(now * this.profile.speed);
            this.lastFrameTime = now;
        }

        this.frame = window.requestAnimationFrame(this.animate);
    }

    draw(t) {
        const referenceHeight = this.height;
        const yLimit = referenceHeight * .42;
        const masterAmplitude = 1 + Math.sin(t * 1.2) * .15;
        const finalAmplitude = yLimit * masterAmplitude;
        const segments = this.segments || this.getSegmentCount();
        const centerY = this.height * .5;

        this.ctx.clearRect(0, 0, this.width, this.height);
        this.ctx.lineWidth = this.width < 768 ? .85 : 1.15;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        this.threads.forEach(thread => {
            this.drawThread(thread, t, finalAmplitude, segments, centerY);
        });
    }

    drawThread(thread, t, finalAmplitude, segments, centerY) {
        const threadTime = t + thread.timeOffset;
        const baseWidth = finalAmplitude * .184;

        this.ctx.beginPath();
        this.ctx.strokeStyle = `rgba(${thread.color[0]}, ${thread.color[1]}, ${thread.color[2]}, .78)`;

        for (let j = 0; j <= segments; j++) {
            const xProgress = j / segments;
            const x = this.width * .5 + (xProgress - .5) * this.width * 1.65;
            const slowWave = Math.sin(xProgress * Math.PI * 2 - threadTime * .5) * finalAmplitude * .35;
            const braidAngle = xProgress * Math.PI * 2 + threadTime + thread.phase;
            const pinchEffect = .5 - .5 * Math.cos(xProgress * Math.PI * 2);
            const pinch = baseWidth + pinchEffect * finalAmplitude * .55;
            const y = centerY + Math.sin(braidAngle) * pinch + slowWave;

            if (j === 0) {
                this.ctx.moveTo(x, y);
            } else {
                this.ctx.lineTo(x, y);
            }
        }

        this.ctx.stroke();
    }
}
